import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get } from 'store/request';

export const rta: Application = {
  client_id: 'rta',
  app_name: '自建 RTA 工具',
  app_icon: 'https://unionplatform-75179.gzc.vod.tencent-cloud.com/webstore/rta.png',
  app_desc: '您可通过官方API接口，自行搭建RTA投放平台，实现定制化的投放场景',
  category: 2,
};

export const fetchApplicationList = createAsyncThunk('application/fetchList', () => get('/application'));

export const fetchApplicationById = createAsyncThunk('application/fetchById', (id: string) =>
  get(`/application/${id}`)
);

export const fetchApplicationAccounts = createAsyncThunk('application/applicationsAccounts', (id: string) =>
  get(`/my_applications/app_accounts/${id}`)
);

export const fetchAppInfo = createAsyncThunk('application/fetchAppInfo', (id: string) =>
  get(`/developer_service/app_info?client_id=${id}`)
);

export const fetchAppWxList = createAsyncThunk('application/fetchAppWxList', (id: string) =>
  get(`/developer_service/get_wx_info?client_id=${id}`)
);

export interface Application {
  client_id: string;
  redirect_uri?: string;
  app_icon: string;
  app_name: string;
  app_highlights?: string;
  feedback_data?: string;
  client_auth_count?: number;
  version?: string;
  provider?: string;
  app_desc: string;
  app_image_list?: { url: string; type: 'video' | 'image'; name: string }[];
  state?: string;
  // 应用级别，1 显示在首页
  level?: number;
  // 应用类别 0 广告投放; 1 私域; 2 RTA; 11 内容服务; 12 数据服务 13 代运营服务
  category: 0 | 1 | 2 | 11 | 12 | 13;
  client_bind_status?: boolean; // 应用是否被绑定到服务商 true 绑定了， false 没绑定
  extra?: {
    label: string;
    value: string;
  }[];
  // 是否限制同主体
  same_enterprise?: number;
  // 应用授权类型，0 普通，1 私域快速登录，2 暂不使用，3 隐式授权
  app_auth_type?: 0 | 1 | 2 | 3;
  status?: number;
}

interface Account {
  client_id: string;
  account_id: string;
  account_name: string;
  // 0 待授权, 1 已授权
  status: 0 | 1;
  // 这个广告账号能否被授权
  auto_authed: boolean;
  status_text: string;
  role: number;
}

interface AppInfo {
  name: string;
  desc: string;
  scope: string;
}

interface ApplicationState {
  list: {
    [key: string]: Application;
  };
  touFang: string[][];
  touFangList: string[];
  siYu: string[];
  rta: string[];
  appAccountsMap: {
    [key: string]: Account[];
  };
  appAccountsLoading: boolean;
  appInfoMap: {
    [key: string]: AppInfo[];
  };
  appInfoLoading: boolean;
  appWxInfo: {
    list: string[];
    organize: string;
  };
  appWxInfoLoading: boolean;
}

const initialState: ApplicationState = {
  list: {},
  touFang: [],
  touFangList: [],
  siYu: [],
  rta: [],
  appAccountsMap: {},
  appAccountsLoading: true,
  appInfoMap: {},
  appInfoLoading: true,
  appWxInfo: {
    list: [],
    organize: '',
  },
  appWxInfoLoading: true,
};

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchApplicationById.fulfilled, (state, { payload }: PayloadAction<Application>) => {
      state.list[payload.client_id] = payload;
    });
    builder.addCase(fetchApplicationList.fulfilled, (state, { payload }: PayloadAction<{ list: Application[] }>) => {
      const touFang: string[] = [];
      // const touFangList: string[] = [];
      payload?.list.forEach((item) => {
        if (item.level === 1) {
          switch (item.category) {
            case 0:
              touFang.push(item.client_id);
              break;
            case 1:
              state.siYu.push(item.client_id);
              break;
            case 2:
              state.rta.push(item.client_id);
              break;
          }
        }
        state.list[item.client_id] = item;
      });
      const temp: string[][] = new Array(Math.ceil(touFang.length / 9));
      for (let i = 0; i < touFang.length; i++) {
        const index = Math.floor(i / 9);
        if (!temp[index]) {
          temp[index] = [];
        }
        temp[index].push(touFang[i]);
      }
      state.touFang = temp;
      state.touFangList = touFang;
      state.list[rta.client_id] = rta;
      state.rta.push(rta.client_id);
    });

    builder.addCase(fetchApplicationAccounts.pending, (state) => {
      state.appAccountsLoading = true;
    });
    builder.addCase(
      fetchApplicationAccounts.fulfilled,
      (state, { payload, meta }: PayloadAction<{ list: Account[] }, string, { arg: string }>) => {
        state.appAccountsMap[meta.arg] = payload.list?.map((account) => ({
          ...account,
          status_text: account.status ? '已开通' : '未开通',
        }));
        state.appAccountsLoading = false;
      }
    );
    builder.addCase(fetchApplicationAccounts.rejected, (state) => {
      state.appAccountsLoading = false;
    });

    builder.addCase(fetchAppInfo.pending, (state) => {
      state.appInfoLoading = true;
    });
    builder.addCase(
      fetchAppInfo.fulfilled,
      (state, { payload, meta }: PayloadAction<{ authority_list: AppInfo[] }, string, { arg: string }>) => {
        state.appInfoMap[meta.arg] = payload?.authority_list?.filter(({ name }) => name !== '账号管理');
        state.appInfoLoading = false;
      }
    );
    builder.addCase(fetchAppInfo.rejected, (state) => {
      state.appInfoLoading = false;
    });

    builder.addCase(fetchAppWxList.pending, (state) => {
      state.appWxInfo = { list: [], organize: '' };
      state.appWxInfoLoading = true;
    });
    builder.addCase(
      fetchAppWxList.fulfilled,
      (
        state,
        { payload }: PayloadAction<{ list: { wechat_app_id: string }[]; organize: string }, string, { arg: string }>
      ) => {
        state.appWxInfo.list = payload?.list?.map((item) => item.wechat_app_id);
        state.appWxInfo.organize = payload.organize;
        state.appWxInfoLoading = false;
      }
    );
    builder.addCase(fetchAppWxList.rejected, (state) => {
      state.appWxInfoLoading = false;
    });
  },
});

export default applicationSlice;
